import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  HeartPulse, Upload, Calendar, TrendingUp, TrendingDown,
  Minus, ChevronRight, FileText, CheckCircle2, Circle, AlertCircle,
  Trash2, X, Loader2, Link2, RefreshCw, Unlink, Video, LayoutGrid, Send,
  Download, Mail, Plus, ToggleLeft, ToggleRight, Globe, Settings
} from 'lucide-react';
import { authFetch, downloadFile } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ 'Content-Type': 'application/json' });

const SENTIMENT_COLORS = {
  'Very Positive': { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', bar: 'bg-emerald-500' },
  'Positive': { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', bar: 'bg-green-500' },
  'Neutral': { bg: 'bg-surface-page', text: 'text-text-secondary', border: 'border-border-subtle', bar: 'bg-slate-400' },
  'Concerned': { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', bar: 'bg-amber-500' },
  'Negative': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', bar: 'bg-red-500' },
};

const SentimentBadge = ({ label, score }) => {
  const colors = SENTIMENT_COLORS[label] || SENTIMENT_COLORS['Neutral'];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${colors.bg} ${colors.text} ${colors.border} border`}>
      <span className={`w-2 h-2 rounded-full ${colors.bar}`} />
      {score}/10 {label}
    </span>
  );
};

const getScoreColor = (score) => {
  if (score >= 8) return '#10b981';
  if (score >= 6) return '#22c55e';
  if (score >= 4) return '#f59e0b';
  return '#ef4444';
};

const getPriorityClass = (priority) => {
  if (priority === 'high') return 'bg-red-500';
  if (priority === 'medium') return 'bg-amber-500';
  return 'bg-slate-400';
};

const ScoreGauge = ({ score, size = 'md' }) => {
  const radius = size === 'lg' ? 54 : 36;
  const stroke = size === 'lg' ? 8 : 6;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 10) * circumference;
  const color = getScoreColor(score);
  const dim = (radius + stroke) * 2;

  return (
    <svg width={dim} height={dim} className="transform -rotate-90">
      <circle cx={radius + stroke} cy={radius + stroke} r={radius} fill="none" stroke="#e5e7eb" strokeWidth={stroke} />
      <circle cx={radius + stroke} cy={radius + stroke} r={radius} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={circumference} strokeDashoffset={circumference - progress} strokeLinecap="round"
        className="transition-all duration-1000 ease-out" />
      <text x={radius + stroke} y={radius + stroke} textAnchor="middle" dominantBaseline="central"
        className="transform rotate-90" style={{ transformOrigin: 'center', fontSize: size === 'lg' ? '20px' : '14px', fontWeight: 700, fill: color }}>
        {score}
      </text>
    </svg>
  );
};

const PriorityDot = ({ priority }) => {
  return <span className={`w-2 h-2 rounded-full ${getPriorityClass(priority)} flex-shrink-0`} />;
};

// Upload Modal
const UploadModal = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({ client_name: '', meeting_title: '', meeting_date: '', transcript_text: '', attendees: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.meeting_title || !form.transcript_text) {
      toast.error('Client name, meeting title, and transcript are required');
      return;
    }
    setLoading(true);
    try {
      const res = await authFetch(`${API}/api/meetings/analyze`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({
          ...form,
          attendees: form.attendees ? form.attendees.split(',').map(a => a.trim()) : []
        })
      });
      if (!res.ok) throw new Error('Analysis failed');
      const data = await res.json();
      toast.success(`Meeting analyzed — Sentiment: ${data.sentiment_score}/10 (${data.sentiment_label})`);
      onSuccess(data);
    } catch (err) {
      toast.error('Failed to analyze transcript. The AI may need a moment — try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="upload-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-bold text-brand">Analyze Meeting Transcript</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-surface-page"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Client Name *</label>
              <Input data-testid="meeting-client-name" value={form.client_name} onChange={e => setForm({...form, client_name: e.target.value})} placeholder="e.g., Acme Corp" />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Meeting Title *</label>
              <Input data-testid="meeting-title" value={form.meeting_title} onChange={e => setForm({...form, meeting_title: e.target.value})} placeholder="e.g., Q2 Campaign Review" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Meeting Date</label>
              <Input data-testid="meeting-date" type="date" value={form.meeting_date} onChange={e => setForm({...form, meeting_date: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Attendees (comma-separated)</label>
              <Input data-testid="meeting-attendees" value={form.attendees} onChange={e => setForm({...form, attendees: e.target.value})} placeholder="John Smith, Sarah Chen" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Google Meet Transcript *</label>
            <Textarea data-testid="meeting-transcript" value={form.transcript_text} onChange={e => setForm({...form, transcript_text: e.target.value})}
              placeholder="Paste the full Google Meet transcript here..." rows={10} className="font-mono text-sm" />
            <p className="text-xs text-text-muted mt-1">Paste the transcript directly from Google Meet. The AI will extract summaries, action items, and sentiment.</p>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
            <Button type="submit" disabled={loading} className="bg-brand hover:bg-brand-hover" data-testid="analyze-meeting-btn">
              {loading ? <><Loader2 className="animate-spin mr-2" size={16} /> Analyzing with AI...</> : <><HeartPulse size={16} className="mr-2" /> Analyze Transcript</>}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Meeting Detail View
const MeetingDetail = ({ meeting, onBack, onDelete, onRefresh }) => {
  const analysis = meeting.analysis || {};
  const [showPushModal, setShowPushModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null);
  const [mondayBoards, setMondayBoards] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [pushing, setPushing] = useState(false);
  const [loadingBoards, setLoadingBoards] = useState(false);

  const fetchMondayBoards = async () => {
    setLoadingBoards(true);
    try {
      const res = await authFetch(`${API}/api/monday/boards`);
      if (res.ok) {
        const data = await res.json();
        setMondayBoards(data.boards || []);
      }
    } catch (e) { console.error('Failed to load Monday boards:', e); }
    finally { setLoadingBoards(false); }
  };

  const openPushModal = () => {
    fetchMondayBoards();
    // Pre-select all unpushed action items
    const unpushed = (meeting.action_items || [])
      .map((item, idx) => ({ ...item, idx }))
      .filter(item => !item.pushed_to_monday)
      .map(item => item.idx);
    setSelectedItems(unpushed);
    setSelectedBoard('');
    setShowPushModal(true);
  };

  const toggleItem = (idx) => {
    setSelectedItems(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  const pushToMonday = async () => {
    if (!selectedBoard) { toast.error('Select a Monday.com board'); return; }
    if (selectedItems.length === 0) { toast.error('Select at least one action item'); return; }

    setPushing(true);
    try {
      const res = await authFetch(`${API}/api/meetings/${meeting.id}/push-to-monday`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({
          board_id: selectedBoard,
          action_item_indices: selectedItems
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Push failed');
      }
      const data = await res.json();
      toast.success(`Pushed ${data.pushed_count} action item(s) to Monday.com`);
      setShowPushModal(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.message || 'Failed to push to Monday.com');
    } finally {
      setPushing(false);
    }
  };

  const actionItems = meeting.action_items || [];
  const hasUnpushedItems = actionItems.some(item => !item.pushed_to_monday);

  return (
    <div data-testid="meeting-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-brand hover:underline mb-4" data-testid="back-to-meetings">
        &larr; Back to Meetings
      </button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-text-primary">{meeting.meeting_title}</h2>
          <p className="text-sm text-text-tertiary mt-1">
            {meeting.client_name} &middot; {meeting.meeting_date}
            {meeting.attendees?.length > 0 && <> &middot; {meeting.attendees.join(', ')}</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                await downloadFile(
                  `/api/meetings/${meeting.id}/followup-pdf`,
                  `FollowUp_${meeting.client_name?.replace(/\s/g,'_')}_${meeting.meeting_date}.pdf`
                );
                toast.success('Follow-up PDF downloaded');
              } catch (e) {
                toast.error(e.message);
              }
            }}
            className="border-brand/30 text-brand hover:bg-brand/5"
            data-testid="download-followup-pdf-btn"
          >
            <Download size={14} className="mr-1.5" />
            Export PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowEmailModal(true)}
            className="border-brand/30 text-brand hover:bg-brand/5"
            data-testid="email-followup-btn"
          >
            <Mail size={14} className="mr-1.5" />
            Email Follow-Up
          </Button>
          <SentimentBadge label={meeting.sentiment_label} score={meeting.sentiment_score} />
          <button onClick={() => onDelete(meeting.id)} className="p-2 text-text-muted hover:text-red-500 hover:bg-red-50 rounded-lg" data-testid="delete-meeting-btn">
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Summary */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-text-tertiary">Meeting Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-text-secondary leading-relaxed whitespace-pre-wrap">{analysis.summary}</p>
            </CardContent>
          </Card>

          {/* Action Items */}
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-text-tertiary">
                Action Items ({actionItems.length})
              </CardTitle>
              {actionItems.length > 0 && hasUnpushedItems && (
                <Button
                  onClick={openPushModal}
                  variant="outline"
                  size="sm"
                  className="border-brand/30 text-brand hover:bg-brand/5"
                  data-testid="push-to-monday-btn"
                >
                  <LayoutGrid size={14} className="mr-1.5" />
                  Push to Monday
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {actionItems.length === 0 ? (
                <p className="text-sm text-text-muted">No action items identified.</p>
              ) : (
                <div className="space-y-2">
                  {actionItems.map((item, idx) => (
                    <div key={`item-${idx}`} className={`flex items-start gap-3 p-3 rounded-lg border ${
                      item.pushed_to_monday 
                        ? 'bg-brand/5 border-brand/20' 
                        : 'bg-surface-page border-border-subtle'
                    }`} data-testid={`action-item-${idx}`}>
                      <PriorityDot priority={item.priority} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-primary">{item.task}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-text-tertiary">{item.assignee}</span>
                          {item.due && <span className="text-xs text-text-muted">{item.due}</span>}
                          <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                            item.priority === 'high' ? 'bg-red-100 text-red-700' :
                            item.priority === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-surface-page text-text-secondary'
                          }`}>{item.priority}</span>
                        </div>
                      </div>
                      {item.pushed_to_monday ? (
                        <span className="flex items-center gap-1 text-xs text-brand font-medium flex-shrink-0" data-testid={`action-item-${idx}-pushed`}>
                          <LayoutGrid size={12} /> In Monday
                        </span>
                      ) : item.status === 'done' ? (
                        <CheckCircle2 size={18} className="text-emerald-500 flex-shrink-0" />
                      ) : (
                        <Circle size={18} className="text-text-muted flex-shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Transcript */}
          {meeting.transcript_text && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wider text-text-tertiary">Full Transcript</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="text-xs text-text-secondary whitespace-pre-wrap font-mono bg-surface-page p-4 rounded-lg max-h-96 overflow-y-auto">
                  {meeting.transcript_text}
                </pre>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-text-tertiary">Sentiment Analysis</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center">
              <ScoreGauge score={meeting.sentiment_score} size="lg" />
              <p className="text-lg font-bold mt-2" style={{ color: getScoreColor(meeting.sentiment_score) }}>
                {meeting.sentiment_label}
              </p>
              <p className="text-xs text-text-tertiary text-center mt-2">{analysis.sentiment_notes}</p>
            </CardContent>
          </Card>

          {(analysis.key_topics || []).length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wider text-text-tertiary">Key Topics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1.5">
                  {analysis.key_topics.map((t, i) => (
                    <span key={`item-${i}`} className="px-2 py-1 text-xs font-medium bg-brand/10 text-brand rounded-full">{t}</span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {(analysis.client_concerns || []).length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wider text-text-tertiary flex items-center gap-1.5">
                  <AlertCircle size={14} className="text-amber-500" /> Client Concerns
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1.5">
                  {analysis.client_concerns.map((c, i) => (
                    <li key={`item-${i}`} className="text-xs text-amber-800 bg-amber-50 p-2 rounded-lg">{c}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {(analysis.next_meeting_agenda || []).length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wider text-text-tertiary">Next Meeting Agenda</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1.5">
                  {analysis.next_meeting_agenda.map((a, i) => (
                    <li key={`item-${i}`} className="text-xs text-text-secondary flex items-start gap-2">
                      <ChevronRight size={12} className="text-brand mt-0.5 flex-shrink-0" />
                      {a}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Push to Monday Modal */}
      {showPushModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="push-monday-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg m-4">
            <div className="flex items-center justify-between p-5 border-b">
              <div className="flex items-center gap-2">
                <LayoutGrid size={18} className="text-brand" />
                <h2 className="text-lg font-bold text-text-primary">Push to Monday.com</h2>
              </div>
              <button onClick={() => setShowPushModal(false)} className="p-1 rounded-lg hover:bg-surface-page">
                <X size={20} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Board Selector */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1.5">Select Board</label>
                {loadingBoards ? (
                  <div className="flex items-center gap-2 text-sm text-text-muted py-2">
                    <Loader2 size={14} className="animate-spin" /> Loading boards...
                  </div>
                ) : (
                  <select
                    value={selectedBoard}
                    onChange={(e) => setSelectedBoard(e.target.value)}
                    className="w-full px-3 py-2 border border-input rounded-xl bg-white focus:ring-2 focus:ring-brand text-sm"
                    data-testid="push-monday-board-select"
                  >
                    <option value="">Choose a board...</option>
                    {mondayBoards.map(b => (
                      <option key={b.id} value={b.id}>
                        {b.name} {b.workspace?.name ? `(${b.workspace.name})` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Action Items Checkboxes */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-text-secondary">Select Action Items</label>
                  <button
                    type="button"
                    onClick={() => {
                      const allUnpushed = actionItems
                        .map((item, idx) => ({ ...item, idx }))
                        .filter(item => !item.pushed_to_monday)
                        .map(item => item.idx);
                      setSelectedItems(selectedItems.length === allUnpushed.length ? [] : allUnpushed);
                    }}
                    className="text-xs text-brand hover:underline"
                    data-testid="push-monday-toggle-all"
                  >
                    {selectedItems.length === actionItems.filter(i => !i.pushed_to_monday).length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="space-y-1.5 max-h-56 overflow-y-auto">
                  {actionItems.map((item, idx) => (
                    <label
                      key={`item-${idx}`}
                      className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                        item.pushed_to_monday
                          ? 'bg-surface-page border-border-subtle opacity-50 cursor-not-allowed'
                          : selectedItems.includes(idx)
                            ? 'bg-brand/5 border-brand/30'
                            : 'bg-white border-border-subtle hover:border-brand/20'
                      }`}
                      data-testid={`push-monday-item-${idx}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(idx)}
                        onChange={() => !item.pushed_to_monday && toggleItem(idx)}
                        disabled={item.pushed_to_monday}
                        className="mt-0.5 rounded border-border-strong text-brand focus:ring-brand"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-text-primary">{item.task}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-text-tertiary">{item.assignee}</span>
                          <PriorityDot priority={item.priority} />
                          {item.pushed_to_monday && (
                            <span className="text-xs text-brand font-medium">Already pushed</span>
                          )}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-5 border-t">
              <Button variant="outline" onClick={() => setShowPushModal(false)} disabled={pushing}>
                Cancel
              </Button>
              <Button
                onClick={pushToMonday}
                disabled={pushing || !selectedBoard || selectedItems.length === 0}
                className="bg-brand hover:bg-brand-hover"
                data-testid="push-monday-confirm-btn"
              >
                {pushing ? (
                  <><Loader2 size={14} className="animate-spin mr-1.5" /> Pushing...</>
                ) : (
                  <><Send size={14} className="mr-1.5" /> Push {selectedItems.length} Item{selectedItems.length !== 1 ? 's' : ''}</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Email Follow-Up Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="email-followup-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg m-4">
            <div className="flex items-center justify-between p-5 border-b">
              <div className="flex items-center gap-2">
                <Mail size={18} className="text-brand" />
                <h2 className="text-lg font-bold text-text-primary">Email Follow-Up</h2>
              </div>
              <button onClick={() => { setShowEmailModal(false); setEmailPreview(null); }} className="p-1 rounded-lg hover:bg-surface-page">
                <X size={20} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1.5">Recipients</label>
                <Input
                  placeholder="email@example.com, another@example.com"
                  value={emailRecipients}
                  onChange={(e) => setEmailRecipients(e.target.value)}
                  data-testid="email-recipients-input"
                />
                <p className="text-xs text-text-tertiary mt-1">Comma-separated email addresses</p>
              </div>

              {/* Preview summary */}
              <div className="bg-surface-page rounded-lg p-4 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-text-tertiary">Preview</p>
                <p className="text-sm font-semibold text-text-primary">Follow-Up: {meeting.meeting_title}</p>
                <p className="text-xs text-text-secondary">{meeting.client_name} &middot; {meeting.meeting_date}</p>
                <p className="text-xs text-text-secondary line-clamp-3">{analysis.summary?.substring(0, 200)}...</p>
                <p className="text-xs text-text-muted">{actionItems.length} action item(s) included</p>
              </div>

              {emailPreview && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-emerald-700 flex items-center gap-1.5">
                    <CheckCircle2 size={14} /> Email prepared successfully
                  </p>
                  <p className="text-xs text-emerald-600 mt-1">Subject: {emailPreview.subject}</p>
                  <p className="text-xs text-text-tertiary mt-0.5">
                    Note: Email delivery requires email service integration (SendGrid). The follow-up has been saved and can be copied.
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-5 border-t">
              <Button variant="outline" onClick={() => { setShowEmailModal(false); setEmailPreview(null); }} disabled={sendingEmail}>
                {emailPreview ? 'Close' : 'Cancel'}
              </Button>
              {!emailPreview && (
                <Button
                  onClick={async () => {
                    const recipients = emailRecipients.split(',').map(e => e.trim()).filter(Boolean);
                    if (recipients.length === 0) { toast.error('Enter at least one email'); return; }
                    setSendingEmail(true);
                    try {
                      const res = await authFetch(`${API}/api/meetings/${meeting.id}/send-followup`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ recipients })
                      });
                      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Failed'); }
                      const data = await res.json();
                      setEmailPreview(data.email);
                      toast.success(data.message);
                    } catch (e) { toast.error(e.message); }
                    finally { setSendingEmail(false); }
                  }}
                  disabled={sendingEmail || !emailRecipients.trim()}
                  className="bg-brand hover:bg-brand-hover"
                  data-testid="send-followup-email-btn"
                >
                  {sendingEmail ? (
                    <><Loader2 size={14} className="animate-spin mr-1.5" /> Preparing...</>
                  ) : (
                    <><Mail size={14} className="mr-1.5" /> Prepare Follow-Up</>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Page
const MeetingsPulse = ({ user }) => {
  const [view, setView] = useState('dashboard'); // dashboard, detail
  const [meetings, setMeetings] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [loading, setLoading] = useState(true);
  const [calendarStatus, setCalendarStatus] = useState({ connected: false });
  const [syncing, setSyncing] = useState(false);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [calendarFeeds, setCalendarFeeds] = useState([]);
  const [googleEnabled, setGoogleEnabled] = useState(true);
  const [showAddFeed, setShowAddFeed] = useState(false);
  const [feedName, setFeedName] = useState('');
  const [feedUrl, setFeedUrl] = useState('');
  const [addingFeed, setAddingFeed] = useState(false);
  const [showCalSettings, setShowCalSettings] = useState(false);

  const fetchEvents = useCallback(async () => {
    setLoadingEvents(true);
    try {
      const res = await authFetch(`${API}/api/calendar/events/today`);
      if (res.ok) {
        const data = await res.json();
        setUpcomingEvents(data.events || []);
      }
    } catch (e) { console.error('Failed to load events:', e); }
    finally { setLoadingEvents(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API]);

  const fetchFeeds = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api/calendar/feeds`);
      if (res.ok) {
        const data = await res.json();
        setCalendarFeeds(data.feeds || []);
        setGoogleEnabled(data.google?.enabled !== false);
      }
    } catch (e) { console.error('Failed to load feeds:', e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API]);

  const fetchData = useCallback(async () => {
    try {
      const [meetRes, dashRes, calRes] = await Promise.all([
        authFetch(`${API}/api/meetings`),
        authFetch(`${API}/api/meetings/pulse/dashboard`),
        authFetch(`${API}/api/calendar/auth/status`)
      ]);
      if (meetRes.ok) setMeetings((await meetRes.json()).meetings);
      if (dashRes.ok) setDashboard(await dashRes.json());
      if (calRes.ok) {
        const calData = await calRes.json();
        setCalendarStatus(calData);
        if (calData.connected) fetchEvents();
      }
      fetchFeeds();
    } catch (e) {
      toast.error('Failed to load meeting data');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchEvents]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const connectCalendar = async () => {
    try {
      const res = await authFetch(`${API}/api/calendar/auth/start`);
      if (res.ok) {
        const { authorization_url } = await res.json();
        window.open(authorization_url, '_blank', 'width=600,height=700');
        toast.info('Complete Google sign-in in the popup window, then refresh this page.');
      }
    } catch (e) {
      toast.error('Failed to start calendar connection');
    }
  };

  const disconnectCalendar = async () => {
    if (!window.confirm('Disconnect Google Calendar? Auto-sync will stop.')) return;
    try {
      await authFetch(`${API}/api/calendar/disconnect`, { method: 'POST' });
      setCalendarStatus({ connected: false });
      setUpcomingEvents([]);
      toast.success('Google Calendar disconnected');
    } catch (e) {
      toast.error('Failed to disconnect');
    }
  };

  const addFeed = async () => {
    if (!feedUrl.trim()) { toast.error('URL is required'); return; }
    setAddingFeed(true);
    try {
      const res = await authFetch(`${API}/api/calendar/feeds`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: feedUrl.trim(), name: feedName.trim() || 'Calendar Feed' }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to add feed');
      }
      const feed = await res.json();
      toast.success(`Calendar feed added (${feed.event_count} events found today)`);
      setFeedName(''); setFeedUrl(''); setShowAddFeed(false);
      fetchFeeds();
      fetchEvents();
    } catch (e) { toast.error(e.message); }
    finally { setAddingFeed(false); }
  };

  const toggleFeed = async (feedId, enabled) => {
    try {
      await authFetch(`${API}/api/calendar/feeds/${feedId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      setCalendarFeeds(prev => prev.map(f => f.id === feedId ? { ...f, enabled } : f));
      fetchEvents();
    } catch (e) { toast.error('Failed to update feed'); }
  };

  const deleteFeed = async (feedId) => {
    if (!window.confirm('Remove this calendar feed?')) return;
    try {
      await authFetch(`${API}/api/calendar/feeds/${feedId}`, { method: 'DELETE' });
      setCalendarFeeds(prev => prev.filter(f => f.id !== feedId));
      fetchEvents();
      toast.success('Feed removed');
    } catch (e) { toast.error('Failed to delete feed'); }
  };

  const toggleGoogle = async (enabled) => {
    try {
      await authFetch(`${API}/api/calendar/sources/google`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      setGoogleEnabled(enabled);
      fetchEvents();
    } catch (e) { toast.error('Failed to toggle'); }
  };


  const syncTranscripts = async () => {
    setSyncing(true);
    try {
      const res = await authFetch(`${API}/api/calendar/sync-transcripts`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        if (data.synced_count > 0) {
          toast.success(`Synced ${data.synced_count} meeting transcript(s)`);
          fetchData();
        } else {
          toast.info(`Checked ${data.checked_count} recent meeting(s) — no new transcripts found`);
        }
      }
    } catch (e) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const viewMeeting = async (meetingId) => {
    try {
      const res = await authFetch(`${API}/api/meetings/${meetingId}`);
      if (res.ok) {
        setSelectedMeeting(await res.json());
        setView('detail');
      }
    } catch (e) {
      toast.error('Failed to load meeting');
    }
  };

  const deleteMeeting = async (meetingId) => {
    if (!window.confirm('Delete this meeting record?')) return;
    try {
      await authFetch(`${API}/api/meetings/${meetingId}`, { method: 'DELETE' });
      toast.success('Meeting deleted');
      setView('dashboard');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  if (view === 'detail' && selectedMeeting) {
    return <MeetingDetail meeting={selectedMeeting} onBack={() => { setView('dashboard'); fetchData(); }} onDelete={deleteMeeting} onRefresh={() => viewMeeting(selectedMeeting.id)} />;
  }

  const TrendIcon = ({ score }) => {
    if (score >= 7) return <TrendingUp size={14} className="text-emerald-500" />;
    if (score >= 4) return <Minus size={14} className="text-text-muted" />;
    return <TrendingDown size={14} className="text-red-500" />;
  };

  return (
    <div data-testid="meetings-pulse-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-text-primary">The Pulse</h1>
          <p className="text-sm text-text-tertiary mt-1">Client meeting intelligence — AI-powered transcripts, sentiment & follow-ups</p>
        </div>
        <div className="flex items-center gap-2">
          {calendarStatus.connected && (
            <Button onClick={syncTranscripts} variant="outline" disabled={syncing} data-testid="sync-transcripts-btn"
              className="border-brand/30 text-brand hover:bg-brand/5">
              {syncing ? <Loader2 size={16} className="animate-spin mr-2" /> : <RefreshCw size={16} className="mr-2" />}
              Sync Transcripts
            </Button>
          )}
          <Button onClick={() => setShowUpload(true)} className="bg-brand hover:bg-brand-hover" data-testid="upload-transcript-btn">
            <Upload size={16} className="mr-2" /> Analyze Transcript
          </Button>
        </div>
      </div>

      {/* Calendar Sources */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-brand" />
              <span className="text-sm font-semibold text-text-primary">Calendar Sources</span>
              <span className="text-xs text-text-tertiary">({upcomingEvents.length} events today)</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowCalSettings(!showCalSettings)} data-testid="cal-settings-btn">
                <Settings size={14} className="mr-1" /> {showCalSettings ? 'Hide' : 'Manage'}
              </Button>
            </div>
          </div>

          {showCalSettings && (
            <div className="space-y-3 border-t border-border-subtle pt-3">
              {/* Google Calendar */}
              <div className="flex items-center justify-between p-3 bg-surface-page rounded-lg" data-testid="google-cal-source">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Calendar size={14} className="text-blue-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary">Google Calendar</p>
                    <p className="text-xs text-text-tertiary">
                      {calendarStatus.connected ? 'Connected — auto-syncs transcripts' : 'Not connected'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {calendarStatus.connected ? (
                    <>
                      <button onClick={() => toggleGoogle(!googleEnabled)} className="p-1" data-testid="toggle-google-cal">
                        {googleEnabled
                          ? <ToggleRight size={24} className="text-brand" />
                          : <ToggleLeft size={24} className="text-text-muted" />
                        }
                      </button>
                      <button onClick={disconnectCalendar} className="text-xs text-text-muted hover:text-red-500" data-testid="disconnect-calendar-btn">
                        <Unlink size={14} />
                      </button>
                    </>
                  ) : (
                    <Button size="sm" onClick={connectCalendar} className="bg-brand hover:bg-brand-hover" data-testid="connect-calendar-btn">
                      <Link2 size={14} className="mr-1" /> Connect
                    </Button>
                  )}
                </div>
              </div>

              {/* ICS Feeds */}
              {calendarFeeds.map(feed => (
                <div key={feed.id} className="flex items-center justify-between p-3 bg-surface-page rounded-lg" data-testid={`feed-${feed.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                      <Globe size={14} className="text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-text-primary">{feed.name}</p>
                      <p className="text-xs text-text-tertiary truncate max-w-[280px]">{feed.url}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => toggleFeed(feed.id, !feed.enabled)} className="p-1" data-testid={`toggle-feed-${feed.id}`}>
                      {feed.enabled
                        ? <ToggleRight size={24} className="text-brand" />
                        : <ToggleLeft size={24} className="text-text-muted" />
                      }
                    </button>
                    <button onClick={() => deleteFeed(feed.id)} className="p-1 text-text-muted hover:text-red-500" data-testid={`delete-feed-${feed.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}

              {/* Add Feed Form */}
              {showAddFeed ? (
                <div className="p-3 border border-dashed border-border-subtle rounded-lg space-y-2" data-testid="add-feed-form">
                  <Input
                    placeholder="Feed name (e.g., BCM Shared Calendar)"
                    value={feedName}
                    onChange={e => setFeedName(e.target.value)}
                    data-testid="feed-name-input"
                  />
                  <Input
                    placeholder="ICS/iCal URL (https://...)"
                    value={feedUrl}
                    onChange={e => setFeedUrl(e.target.value)}
                    data-testid="feed-url-input"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => { setShowAddFeed(false); setFeedName(''); setFeedUrl(''); }}>Cancel</Button>
                    <Button size="sm" onClick={addFeed} disabled={addingFeed} className="bg-brand hover:bg-brand-hover" data-testid="save-feed-btn">
                      {addingFeed ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Plus size={14} className="mr-1" />}
                      Add Feed
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowAddFeed(true)}
                  className="w-full p-3 border border-dashed border-border-subtle rounded-lg text-sm text-text-muted hover:text-brand hover:border-brand/30 transition-colors flex items-center justify-center gap-2"
                  data-testid="add-feed-btn"
                >
                  <Plus size={14} /> Add Calendar Feed (ICS/iCal URL)
                </button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Today's Meetings (from any connected source) */}
      {upcomingEvents.length > 0 && (
        <div className="mb-8">
          <h3 className="text-sm font-bold uppercase tracking-wider text-text-muted mb-3">Today's Meetings</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {upcomingEvents.slice(0, 6).map((ev, idx) => {
              const startTime = ev.start?.dateTime ? new Date(ev.start.dateTime) : null;
              const hasGoogleMeet = !!(ev.hangoutLink || ev.conferenceData);
              const isIcsFeed = ev.source === 'ics_feed';
              return (
                <Card key={ev.id || idx} className="hover:shadow-sm transition-shadow">
                  <CardContent className="py-3 px-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isIcsFeed ? 'bg-amber-50 text-amber-500'
                        : hasGoogleMeet ? 'bg-blue-50 text-blue-500'
                        : 'bg-surface-page text-text-muted'
                      }`}>
                        {isIcsFeed ? <Globe size={14} /> : hasGoogleMeet ? <Video size={14} /> : <Calendar size={14} />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-text-primary truncate">{ev.summary || 'No Title'}</p>
                        <p className="text-xs text-text-tertiary mt-0.5">
                          {startTime ? startTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : ''}
                          {startTime ? ` at ${startTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : ''}
                        </p>
                        {ev.source_name && (
                          <p className="text-[10px] text-text-muted mt-0.5">{ev.source_name}</p>
                        )}
                        {(ev.attendees || []).length > 0 && (
                          <p className="text-xs text-text-muted mt-0.5 truncate">{ev.attendees.slice(0, 3).map(a => a.email?.split('@')[0]).join(', ')}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Dashboard Cards */}
      {dashboard && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="border-l-4 border-l-nordic">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-text-muted">Agency Health</p>
                  <p className="text-3xl font-semibold mt-1" style={{ color: getScoreColor(dashboard.agency_health_score) }}>
                    {dashboard.agency_health_score}
                  </p>
                </div>
                <ScoreGauge score={dashboard.agency_health_score} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-5 pb-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-text-muted">Active Clients</p>
              <p className="text-3xl font-semibold text-brand mt-1">{dashboard.total_clients}</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-5 pb-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-text-muted">Total Meetings</p>
              <p className="text-3xl font-semibold text-brand mt-1">{dashboard.total_meetings}</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-5 pb-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-text-muted">Avg Sentiment</p>
              <p className="text-3xl font-semibold mt-1" style={{ color: getScoreColor(dashboard.agency_health_score) }}>
                {dashboard.agency_health_score}/10
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Client Health Cards */}
      {dashboard?.clients?.length > 0 && (
        <div className="mb-8">
          <h3 className="text-sm font-bold uppercase tracking-wider text-text-muted mb-4">Client Health</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {dashboard.clients.map((client) => (
              <Card key={client.client_name} className="hover:shadow-md transition-shadow cursor-pointer" data-testid={`client-card-${client.client_name}`}>
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-text-primary">{client.client_name}</h4>
                    <SentimentBadge label={client.latest_label} score={Math.round(client.avg_sentiment * 10) / 10} />
                  </div>
                  <div className="flex items-center gap-4 text-xs text-text-tertiary">
                    <span className="flex items-center gap-1"><Calendar size={12} /> {client.meeting_count} meetings</span>
                    <span className="flex items-center gap-1"><FileText size={12} /> {client.pending_actions} actions</span>
                    <span>Last: {client.latest_meeting}</span>
                  </div>
                  {/* Mini sentiment bar */}
                  <div className="mt-3 h-2 bg-surface-page rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500" style={{
                      width: `${(client.avg_sentiment / 10) * 100}%`,
                      backgroundColor: client.avg_sentiment >= 7 ? '#10b981' : client.avg_sentiment >= 4 ? '#f59e0b' : '#ef4444'
                    }} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Meetings List */}
      <div>
        <h3 className="text-sm font-bold uppercase tracking-wider text-text-muted mb-4">Recent Meetings</h3>
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-brand" size={32} /></div>
        ) : meetings.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <HeartPulse size={40} className="mx-auto text-text-muted mb-3" />
              <p className="text-text-tertiary font-medium">No meetings analyzed yet</p>
              <p className="text-sm text-text-muted mt-1">Upload a Google Meet transcript to get started</p>
              <Button onClick={() => setShowUpload(true)} className="mt-4 bg-brand hover:bg-brand-hover" data-testid="empty-state-upload-btn">
                <Upload size={16} className="mr-2" /> Analyze First Transcript
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {meetings.map((m) => (
              <Card key={m.id} className="hover:shadow-md transition-shadow cursor-pointer group" onClick={() => viewMeeting(m.id)} data-testid={`meeting-row-${m.id}`}>
                <CardContent className="py-4 flex items-center gap-4">
                  <div className="flex-shrink-0">
                    <TrendIcon score={m.sentiment_score} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-text-primary text-sm">{m.meeting_title}</span>
                      <span className="text-xs text-text-muted">{m.client_name}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-text-tertiary">
                      <span>{m.meeting_date}</span>
                      <span>{(m.action_items || []).length} action items</span>
                    </div>
                  </div>
                  <SentimentBadge label={m.sentiment_label} score={m.sentiment_score} />
                  <ChevronRight size={16} className="text-text-muted group-hover:text-brand transition-colors" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {showUpload && (
        <UploadModal
          onClose={() => setShowUpload(false)}
          onSuccess={(data) => { setShowUpload(false); fetchData(); }}
        />
      )}
    </div>
  );
};

export default MeetingsPulse;
